import { createFragmentContainer, graphql } from "react-relay";
import { useState, useCallback } from "react";
import JobEvent from "~/components/JobActivityTimeline/JobEvent";

interface StackErrorEvent {
  uuid: string;
  timestamp: string;
  errorDetail: string;
}

type Props = {
  event: StackErrorEvent;
  previousTimestamp: string | null | undefined;
};

const MAX_TRUNCATED_LENGTH = 500;

function JobEventStackErrored({ event, previousTimestamp }: Props) {
  const errorDetail = event.errorDetail || "";
  const shouldTruncate = errorDetail.length > MAX_TRUNCATED_LENGTH;
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  return (
    <JobEvent timestamp={event.timestamp} previousTimestamp={previousTimestamp}>
      <JobEvent.Label>Stack attempted to end the Job</JobEvent.Label>
      <JobEvent.Content>
        <div className="bg-silver border-gray m-0 whitespace-pre-wrap rounded-sm border px-2 py-1 text-sm font-normal">
          <span id={`error-detail-${event.uuid}`}>
            {isExpanded ? errorDetail : errorDetail.slice(0, MAX_TRUNCATED_LENGTH)}
          </span>
          {shouldTruncate && !isExpanded && <span>...</span>}
          {shouldTruncate && (
            <button
              type="button"
              onClick={toggleExpanded}
              className="no-title-tooltip ml-2 font-medium"
              aria-expanded={isExpanded}
              aria-controls={`error-detail-${event.uuid}`}
              aria-label={isExpanded ? "Show full error details" : "Show less error detail"}
            >
              {isExpanded ? "Show less" : "Show more"}
            </button>
          )}
        </div>
      </JobEvent.Content>
    </JobEvent>
  );
}

export default createFragmentContainer(JobEventStackErrored, {
  event: graphql`
    fragment JobEventStackErrored_event on JobEvent {
      ... on JobEvent {
        uuid
        timestamp
        ... on JobEventStackError {
          errorDetail
        }
      }
    }
  `,
});
